import { EDGES } from '@runtime/game/logic/edges'

import { useAppStore } from '@runtime/core/store/appStore'
import {
  COLOR_RGB,
  type Rgb,
  type TokenColorKey,
} from '@runtime/core/theme/colors'
import type {
  PosterThemeConfig,
  ThemePalette,
} from '@runtime/core/theme/themeEngineShared'

const TOKEN_KEYS: TokenColorKey[] = ['RED', 'BLU', 'YEL', 'GRN']
const SIDE_ORDER = ['TOP', 'RIGHT', 'BOTTOM', 'LEFT'] as const

function hexToRgb(hex: unknown): Rgb | null {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex).trim())
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null
}

function rgbToHex(rgb: Rgb | null) {
  if (!rgb) return null
  const [r, g, b] = rgb
  const to2 = (n: number) =>
    Math.max(0, Math.min(255, n | 0))
      .toString(16)
      .padStart(2, '0')
      .toUpperCase()
  return '#' + to2(r) + to2(g) + to2(b)
}

function average(list: Rgb[]) {
  if (!list.length) return null
  let r = 0
  let g = 0
  let b = 0
  for (const px of list) {
    r += px[0]
    g += px[1]
    b += px[2]
  }
  const n = list.length
  return [Math.round(r / n), Math.round(g / n), Math.round(b / n)] as Rgb
}

function persistThemeTokens(tokens: string[]) {
  try {
    useAppStore.getState().setThemeTokens(tokens.slice(0, 4))
  } catch {
    // ignore
  }
}

function normalizePaletteTokens(raw: unknown[]) {
  const out: string[] = []
  for (let i = 0; i < 4; i += 1) {
    const rgb = hexToRgb(raw[i])
    const hex = rgbToHex(rgb)
    if (!hex) return null
    out.push(hex)
  }
  return out
}

function extractPaletteTokens(palette: ThemePalette | undefined) {
  if (!palette) return null

  if (Array.isArray(palette.players) && palette.players.length >= 4) {
    return normalizePaletteTokens(palette.players.slice(0, 4))
  }

  let south
  let east
  let north
  let west

  if (Array.isArray(palette.playersSenw) && palette.playersSenw.length >= 4) {
    ;[south, east, north, west] = palette.playersSenw
  } else if (Array.isArray(palette.senw) && palette.senw.length >= 4) {
    ;[south, east, north, west] = palette.senw
  } else if (palette.cardinal) {
    south = palette.cardinal.south || palette.cardinal.S
    east = palette.cardinal.east || palette.cardinal.E
    north = palette.cardinal.north || palette.cardinal.N
    west = palette.cardinal.west || palette.cardinal.W
  }

  if (!(south && east && north && west)) return null
  return normalizePaletteTokens([east, south, north, west])
}

function applyThemeTokens(tokens: string[]) {
  try {
    for (let i = 0; i < 4; i += 1) {
      const rgb = hexToRgb(tokens[i])
      if (rgb) COLOR_RGB[TOKEN_KEYS[i]] = rgb
    }
  } catch (e) {
    console.warn('[POSTER] failed to set COLOR_RGB from theme', e)
  }

  persistThemeTokens(tokens)
}

function sampleSideColor(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const data = ctx.getImageData(x, y, 1, 1).data
  return [data[0], data[1], data[2]] as Rgb
}

function mapSidesToThemeTokens(
  sideToAvg: Record<(typeof SIDE_ORDER)[number], Rgb>,
  edgesRef: unknown[],
) {
  const byToken: Partial<Record<TokenColorKey, Rgb>> = {}

  for (let i = 0; i < SIDE_ORDER.length; i += 1) {
    const key = String(edgesRef[i] || '').toUpperCase()
    if ((TOKEN_KEYS as string[]).includes(key)) {
      byToken[key as TokenColorKey] = sideToAvg[SIDE_ORDER[i]]
    }
  }

  const mapped = TOKEN_KEYS.map((key) => rgbToHex(byToken[key] || null))
  return mapped.every(Boolean) ? (mapped as string[]) : null
}

function derivePlayersFromTile(url: string, edgesRef: unknown) {
  return new Promise<string[] | null>((resolve) => {
    try {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = function () {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = img.naturalWidth || img.width
          canvas.height = img.naturalHeight || img.height
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            resolve(null)
            return
          }

          ctx.drawImage(img, 0, 0)

          const w = canvas.width
          const h = canvas.height
          const pick = (x: number, y: number) => sampleSideColor(ctx, x, y)

          const sideToAvg = {
            TOP: average([
              pick((w * 0.25) | 0, (h * 0.08) | 0),
              pick((w * 0.5) | 0, (h * 0.08) | 0),
              pick((w * 0.75) | 0, (h * 0.08) | 0),
            ]),
            RIGHT: average([
              pick((w * 0.92) | 0, (h * 0.25) | 0),
              pick((w * 0.92) | 0, (h * 0.5) | 0),
              pick((w * 0.92) | 0, (h * 0.75) | 0),
            ]),
            BOTTOM: average([
              pick((w * 0.25) | 0, (h * 0.92) | 0),
              pick((w * 0.5) | 0, (h * 0.92) | 0),
              pick((w * 0.75) | 0, (h * 0.92) | 0),
            ]),
            LEFT: average([
              pick((w * 0.08) | 0, (h * 0.25) | 0),
              pick((w * 0.08) | 0, (h * 0.5) | 0),
              pick((w * 0.08) | 0, (h * 0.75) | 0),
            ]),
          }

          if (Object.values(sideToAvg).some((v) => !v)) {
            resolve(null)
            return
          }

          const typedSideToAvg = sideToAvg as Record<
            (typeof SIDE_ORDER)[number],
            Rgb
          >

          if (Array.isArray(edgesRef) && edgesRef.length >= 4) {
            const mapped = mapSidesToThemeTokens(typedSideToAvg, edgesRef)
            if (mapped) {
              resolve(mapped)
              return
            }
          }

          const fallback = [
            typedSideToAvg.RIGHT,
            typedSideToAvg.BOTTOM,
            typedSideToAvg.TOP,
            typedSideToAvg.LEFT,
          ]
            .map(rgbToHex)
            .filter(Boolean)

          resolve(fallback.length === 4 ? (fallback as string[]) : null)
        } catch (e) {
          console.warn('[US] derivePlayersFromTile failed at canvas', e)
          resolve(null)
        }
      }
      img.onerror = function () {
        resolve(null)
      }
      img.src = url
    } catch (e) {
      console.warn('[US] derivePlayersFromTile error', e)
      resolve(null)
    }
  })
}

export function applyThemePlayerColors(
  cfg: PosterThemeConfig,
  onDerivedTokensApplied?: () => void,
) {
  const tokens = extractPaletteTokens(cfg.palette)
  if (tokens) {
    applyThemeTokens(tokens)
    return
  }

  if (!cfg.tiles?.center) return

  const tileTwo = Array.isArray(cfg.tiles.set) ? cfg.tiles.set[1] : null
  const derivePromise = tileTwo
    ? derivePlayersFromTile(tileTwo, EDGES.t2 || null).then((out) => {
        if (Array.isArray(out) && out.length >= 4) return out
        return derivePlayersFromTile(cfg.tiles!.center!, EDGES.center || null)
      })
    : derivePlayersFromTile(cfg.tiles.center, EDGES.center || null)

  derivePromise.then((tokensFromTile) => {
    if (!Array.isArray(tokensFromTile) || tokensFromTile.length < 4) return
    applyThemeTokens(tokensFromTile)
    try {
      onDerivedTokensApplied && onDerivedTokensApplied()
    } catch {
      // ignore
    }
  })
}
